import { useEffect, useMemo, useState } from 'react';
import { extForBlob } from '../shared/imageOutputFormats';
import OutputFormatSelect from '../shared/OutputFormatSelect';
import { IconColorPicker, IconSquare, IconSquareOff } from '@tabler/icons-react';
import { notifications } from '@/lib/notifications';
import { useTranslation } from '../../../../../i18n/hooks/useTranslation';
import { toBnDigits } from '../../../../../utils/bnFormat';
import { recordToolUsage } from '../../../../../services/toolsService';
import ToolPageLayout from '../../components/common/ToolPageLayout';
import ToolPreviewPanel from '../../components/common/ToolPreviewPanel';
import ToolCard from '../../components/common/ToolCard';
import Hint from '../../../../../components/common/Hint';
import ToolReadyHint from '../../components/common/ToolReadyHint';
import SignatureFromTextPreview from './SignatureFromTextPreview';
import {
    BACKGROUND_MODES,
    FONT_SIZES,
    MAX_TEXT_LENGTH,
    PEN_COLORS,
    SIGNATURE_FONTS,
    getFontById,
    isSignatureTextValid,
    loadSignatureFont,
    outputFileName,
    renderSignaturePng,
} from './signatureFromTextImageEngine';
import {
    deriveToolStatus,
    downloadBlob,
    getToolStatusText,
} from '../../toolPageUtils';

const inputClass = 'w-full rounded-lg border border-[#ECE8E4] bg-white px-3 py-2 text-[13px] text-[#3D3833] outline-none transition-colors placeholder:text-[#A8A099] focus:border-amber-400 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100 dark:placeholder:text-zinc-500';

/** Glyph-over-label tile, the tray shape the other image tools use. */
function tileBtnClass(active) {
    return `flex min-h-[54px] flex-col items-center justify-center gap-1 rounded-lg border-none px-1 py-1.5 text-[11px] leading-tight transition-all ${
        active
            ? 'bg-white font-semibold text-amber-600 shadow-[0_1px_2px_rgba(28,24,21,0.04)] dark:bg-zinc-900 dark:text-amber-400'
            : 'bg-transparent font-medium text-[#6B635C] dark:text-zinc-400'
    }`;
}

/** The letter drawn at the size it will be written at. */
function SizeGlyph({ size, active }) {
    const color = active ? '#F59E0B' : '#9A9AA3';
    // 48–96px maps onto a 10–20px glyph so the steps stay legible.
    const px = Math.round(10 + ((size - 48) / 48) * 10);
    return (
        <span className="flex h-[22px] w-[22px] items-center justify-center" style={{ color }}>
            <span style={{ fontSize: px, fontWeight: 700, lineHeight: 1 }}>A</span>
        </span>
    );
}

const BACKGROUND_ICONS = { transparent: IconSquareOff, white: IconSquare };

/** Ink outside the presets means the picker tile is the live one. */
function isLightHex(hex) {
    const value = String(hex || '').replace('#', '');
    if (value.length !== 6) return true;
    const r = parseInt(value.slice(0, 2), 16);
    const g = parseInt(value.slice(2, 4), 16);
    const b = parseInt(value.slice(4, 6), 16);
    return (0.299 * r + 0.587 * g + 0.114 * b) / 255 > 0.55;
}

/** What the download comes out as: the lossless default first, then the lighter ones. */
const SIGNATURE_OUTPUT_FORMATS = ['png', 'webp', 'jpg', 'avif'];

export default function SignatureFromTextImage() {
    const { t } = useTranslation();

    const [text, setText] = useState('');
    const [fontId, setFontId] = useState(SIGNATURE_FONTS[0].id);
    const [fontSize, setFontSize] = useState(FONT_SIZES[1]);
    const [color, setColor] = useState(PEN_COLORS[0]);
    const [backgroundMode, setBackgroundMode] = useState('transparent');
    const [format, setFormat] = useState('png');
    const [fontReady, setFontReady] = useState(false);
    const [processing, setProcessing] = useState(false);
    const [downloaded, setDownloaded] = useState(false);
    const [error, setError] = useState(null);

    const font = useMemo(() => getFontById(fontId), [fontId]);
    const canDownload = isSignatureTextValid(text) && fontReady;

    useEffect(() => {
        let cancelled = false;
        setFontReady(false);

        loadSignatureFont(font)
            .then(() => {
                if (!cancelled) setFontReady(true);
            })
            .catch(() => {
                if (!cancelled) setFontReady(false);
            });

        return () => {
            cancelled = true;
        };
    }, [font]);

    const handleDownload = async () => {
        if (!canDownload || processing) return;

        recordToolUsage('image/signature-from-text', { category: 'image' });

        setProcessing(true);
        setError(null);

        try {
            const blob = await renderSignaturePng(text, {
                font,
                fontSize,
                color,
                background: backgroundMode,
                format,
            });
            downloadBlob(blob, outputFileName(extForBlob(blob)));
            setDownloaded(true);
            notifications.show({
                title: t('common.success'),
                message: t('signatureFromTextImage.done'),
                color: 'green',
            });
        } catch {
            const msg = t('signatureFromTextImage.errors.failed');
            setError(msg);
            notifications.show({ title: t('common.error'), message: msg, color: 'red' });
        } finally {
            setProcessing(false);
        }
    };

    const handleReset = () => {
        setText('');
        setFontId(SIGNATURE_FONTS[0].id);
        setFontSize(FONT_SIZES[1]);
        setColor(PEN_COLORS[0]);
        setBackgroundMode('transparent');
        setDownloaded(false);
        setError(null);
    };

    const status = deriveToolStatus({
        hasInput: text.trim().length > 0,
        canRun: canDownload,
        processing,
        hasResult: downloaded,
        hasError: !!error,
    });

    const isCustomColor = !PEN_COLORS.some(
        (penColor) => penColor.toLowerCase() === color.toLowerCase(),
    );

    const statusText = getToolStatusText(status, t, {
        ready: t('signatureFromTextImage.statusReady'),
        success: t('signatureFromTextImage.done'),
    });

    return (
        <ToolPageLayout
            slug="image/signature-from-text"
            subtitleKey="signatureFromTextImage.subtitle"
            status={status}
            statusText={statusText}
            controlsTitle={t('signatureFromTextImage.contentTitle')}
            showTrust={false}
            // The preview column tracks the settings column, so the two sides end
            // level instead of the preview running past the last setting.
            matchSidebarHeight
            onReset={text || downloaded || error ? handleReset : undefined}
            primaryAction={{
                label: t('toolPage.downloadAs', { format: t(`toolPage.outputFormat.formats.${format}`) }),
                onClick: handleDownload,
                disabled: !canDownload || processing,
                loading: processing,
            }}
            actionExtra={(
                <OutputFormatSelect
                    formats={SIGNATURE_OUTPUT_FORMATS}
                    value={format}
                    onChange={setFormat}
                    disabled={processing}
                />
            )}
            controls={(
                <ToolCard title={t('signatureFromTextImage.nameTitle')}>
                    <input
                        type="text"
                        value={text}
                        maxLength={MAX_TEXT_LENGTH}
                        onChange={(e) => {
                            setText(e.target.value);
                            setDownloaded(false);
                            setError(null);
                        }}
                        placeholder={t('signatureFromTextImage.placeholder')}
                        className={inputClass}
                    />
                    <p className="mt-2 text-[11.5px] text-[#A8A099] dark:text-zinc-500">
                        {t('signatureFromTextImage.nameHint', {
                            count: toBnDigits(text.length),
                            max: toBnDigits(MAX_TEXT_LENGTH),
                        })}
                    </p>
                </ToolCard>
            )}
            settings={(
                <div className="space-y-3.5">
                    <ToolCard title={t('signatureFromTextImage.fontTitle')}>
                        {/* Two to a row: each tile is a specimen of its own font,
                            so the list reads as a sample sheet, not a menu. */}
                        <div className="grid grid-cols-2 gap-2">
                            {SIGNATURE_FONTS.map((item) => (
                                <button
                                    key={item.id}
                                    type="button"
                                    onClick={() => {
                                        setFontId(item.id);
                                        setDownloaded(false);
                                        setError(null);
                                    }}
                                    className={`truncate rounded-lg border px-3 py-2 text-center text-[13px] font-semibold transition-colors ${
                                        fontId === item.id
                                            ? 'border-amber-500 bg-amber-50 text-amber-700 dark:border-amber-500 dark:bg-amber-900/30 dark:text-amber-300'
                                            : 'border-[#ECE8E4] text-[#3D3833] hover:border-amber-400/40 dark:border-zinc-700 dark:text-zinc-200'
                                    }`}
                                    style={{ fontFamily: `"${item.family}", cursive` }}
                                >
                                    {t(`signatureFromTextImage.fonts.${item.id}`)}
                                </button>
                            ))}
                        </div>
                    </ToolCard>

                    <ToolCard title={t('signatureFromTextImage.styleTitle')}>
                        <label className="block space-y-1.5">
                            <span className="text-[12.5px] font-semibold text-[#3D3833] dark:text-zinc-200">
                                {t('signatureFromTextImage.sizeTitle')}
                            </span>
                            <div className="grid grid-cols-4 gap-1 rounded-xl bg-[#F7F5F3] p-1 dark:bg-zinc-800">
                                {/* Glyphs only — each "A" is drawn at its size, and the
                                    exact value is in the tooltip / aria-label. */}
                                {FONT_SIZES.map((size) => (
                                    <Hint label={`${toBnDigits(size)}px`} key={size}>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setFontSize(size);
                                                setDownloaded(false);
                                            }}
                                            aria-label={`${toBnDigits(size)}px`}
                                            aria-pressed={fontSize === size}
                                            className={tileBtnClass(fontSize === size)}
                                        >
                                            <SizeGlyph size={size} active={fontSize === size} />
                                        </button>
                                    </Hint>
                                ))}
                            </div>
                        </label>

                        <label className="mt-3 block space-y-1.5">
                            <span className="text-[12.5px] font-semibold text-[#3D3833] dark:text-zinc-200">
                                {t('signatureFromTextImage.colorTitle')}
                            </span>
                            {/* Presets and the free picker on one row — the last
                                tile is the picker, tinted with the ink it holds. */}
                            <div className="flex flex-wrap items-center gap-2">
                                {PEN_COLORS.map((penColor) => (
                                    <button
                                        key={penColor}
                                        type="button"
                                        onClick={() => {
                                            setColor(penColor);
                                            setDownloaded(false);
                                        }}
                                        aria-label={penColor}
                                        className={`h-9 w-9 rounded-lg border-2 transition-transform hover:scale-105 ${
                                            color.toLowerCase() === penColor.toLowerCase()
                                                ? 'border-amber-500 ring-2 ring-amber-400/40'
                                                : 'border-[#ECE8E4] dark:border-zinc-700'
                                        }`}
                                        style={{ backgroundColor: penColor }}
                                    />
                                ))}
                                <Hint label={t('signatureFromTextImage.customColor')}>
                                <span
                                    className={`relative flex h-9 w-9 cursor-pointer items-center justify-center rounded-lg border-2 transition-transform ${
                                        isCustomColor
                                            ? 'border-amber-500 ring-2 ring-amber-400/40'
                                            : 'border-[#ECE8E4] dark:border-zinc-700'
                                    }`}
                                    style={isCustomColor ? { backgroundColor: color } : undefined}
                                >
                                    <IconColorPicker
                                        size={16}
                                        stroke={2}
                                        className={isCustomColor && !isLightHex(color)
                                            ? 'text-white'
                                            : 'text-[#6B635C] dark:text-zinc-300'}
                                    />
                                    <input
                                        type="color"
                                        value={color}
                                        aria-label={t('signatureFromTextImage.customColor')}
                                        onChange={(e) => {
                                            setColor(e.target.value);
                                            setDownloaded(false);
                                        }}
                                        className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
                                    />
                                </span>
                                </Hint>
                            </div>
                        </label>
                    </ToolCard>

                    <ToolCard title={t('signatureFromTextImage.backgroundTitle')}>
                        <div className="grid grid-cols-2 gap-1 rounded-xl bg-[#F7F5F3] p-1 dark:bg-zinc-800">
                            {/* Icons only — the name is in the tooltip and read out
                                via aria-label. */}
                            {BACKGROUND_MODES.map((mode) => {
                                const Icon = BACKGROUND_ICONS[mode] || IconSquare;
                                const label = t(`signatureFromTextImage.backgrounds.${mode}`);
                                return (
                                    <Hint label={label} key={mode}>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setBackgroundMode(mode);
                                                setDownloaded(false);
                                            }}
                                            aria-label={label}
                                            aria-pressed={backgroundMode === mode}
                                            className={tileBtnClass(backgroundMode === mode)}
                                        >
                                            <Icon size={20} stroke={1.8} />
                                        </button>
                                    </Hint>
                                );
                            })}
                        </div>
                    </ToolCard>
                </div>
            )}
            preview={(
                <ToolPreviewPanel
                    title={t('signatureFromTextImage.previewTitle')}
                    fill
                    tall={false}
                    status={status}
                    statusLabel={t(`toolPage.status.${status}`)}
                    idleTitle={t('signatureFromTextImage.previewEmpty')}
                    idleHint={t('signatureFromTextImage.previewEmptyHint')}
                    processingLabel={t('signatureFromTextImage.exporting')}
                    error={error}
                    showIdle={!text.trim() && !error}
                    readyHint={status === 'ready' ? (
                        <ToolReadyHint actionLabel={t('signatureFromTextImage.download')} />
                    ) : null}
                >
                    <SignatureFromTextPreview
                        text={text}
                        fontFamily={font.family}
                        fontSize={fontSize}
                        color={color}
                        backgroundMode={backgroundMode}
                        emptyTitle={t('signatureFromTextImage.previewEmpty')}
                        emptyHint={t('signatureFromTextImage.previewEmptyHint')}
                    />
                </ToolPreviewPanel>
            )}
        />
    );
}
